/**
 * File upload hook with compression and progress tracking
 *
 * Handles file uploads with automatic image and video compression, progress tracking,
 * and error handling. Media files are compressed client-side before upload to reduce
 * bandwidth and storage costs.
 *
 * @param uploadFunction - Async function to handle the actual upload (typically API call)
 *
 * @returns Object containing:
 * - `uploadFiles` - Function to trigger upload
 * - `isUploading` - Boolean indicating upload in progress
 * - `uploadProgress` - Number 0-100 representing upload progress percentage
 * - `error` - Error message string or null
 *
 * @example
 * ```typescript
 * const { uploadMedia } = useForm();
 * const { uploadFiles, isUploading, uploadProgress, error } = useFileUpload(uploadMedia);
 *
 * const handleFileSelect = async (files: File[]) => {
 *   try {
 *     await uploadFiles(files, 5, 'Front of Property');
 *     console.log('Upload successful!');
 *   } catch (err) {
 *     console.error('Upload failed:', err);
 *   }
 * };
 * ```
 *
 * @remarks
 * - Automatically compresses image files (JPEG, PNG, etc.) to max 2MB
 * - Automatically compresses video files to 1080p to preserve detail for AI analysis
 * - Non-media files (PDFs, etc.) are uploaded without modification
 * - Progress: 0-50% for compression, 50-100% for upload
 * - Throws error if upload fails (caller should handle)
 * - Sets `isUploading` to false on completion or error
 */
/* eslint-disable no-console */

import { useState } from 'preact/hooks';
import { compressImage, isImageFile } from '@ox/visits-ui/utils/imageCompression';

export function useFileUpload(
  uploadFunction: (files: File[], sectionNumber: number, mediaType: string) => Promise<void>,
) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const uploadFiles = async (files: File[], sectionNumber: number, mediaType: string) => {
    try {
      setIsUploading(true);
      setError(null);
      setUploadProgress(0);

      const processedFiles: File[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (isImageFile(file)) {
          // Compress images
          const compressed = await compressImage(file);
          processedFiles.push(compressed);
        } else {
          processedFiles.push(file);
        }

        // Update overall progress (first 50% is for processing)
        setUploadProgress(((i + 1) / files.length) * 99);
      }

      // Upload files
      await uploadFunction(processedFiles, sectionNumber, mediaType);
      setUploadProgress(100);
    } catch (err: unknown) {
      setError((err as Error).message || 'Upload failed');
      throw err;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadFiles,
    isUploading,
    uploadProgress,
    error,
  };
}
